import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs } from '@/components/ui/tabs';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { isFormARequired, isFormKRequired } from '@/lib/formApplicability';
import { Shield } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CommunicationLanguageSelector } from './components/CommunicationLanguageSelector';
import { FormNavigationButtons } from './components/FormNavigationButtons';
import { FormProgressIndicator } from './components/FormProgressIndicator';
import { KycTabContent } from './components/KycTabContent';
import { KycTabsList } from './components/KycTabsList';
import { useKycFormData } from './hooks/useKycFormData';
import { useKycFormNavigation } from './hooks/useKycFormNavigation';
import { useKycFormSubmissionHook } from './hooks/useKycFormSubmissionHook';
import {
  FormValidationRef,
  KycFormData,
  KycFormTabsProps,
  ValidationState,
} from './types/kycFormTabs.types';
import {
  getEffectiveValidationState,
  getRequiredFormIds,
  isFormComplete,
} from './utils/kycFormTabs.utils';

const DEFAULT_TAB = 'identification';

/**
 * Orchestrates KYC forms with tab-based navigation, validation, and submission.
 * Supports regular submission and admin edit modes.
 */
export function KycFormTabs({
  token,
  onSubmit,
  onError,
  className,
  identificationValidationState = 'pending',
  formKValidationState = 'pending',
  formAValidationState = 'pending',
  initialData,
  isAdminEdit = false,
}: KycFormTabsProps) {
  const tForms = useFormsTranslation();

  // Refs for child form validation
  const identificationFormRef = useRef<FormValidationRef | null>(null);
  const formKRef = useRef<FormValidationRef | null>(null);
  const formARef = useRef<FormValidationRef | null>(null);

  // Active tab state
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);

  // Memoized isFormComplete with translation function
  const isFormCompleteWithTranslation = useCallback(
    (formId: string, data: KycFormData) => {
      const tFormsFunc = (key: string) => tForms.t(key);
      return isFormComplete(formId, data, tFormsFunc);
    },
    [tForms]
  );

  // Memoized function to get required form IDs from form data
  const getRequiredFormIdsCallback = useCallback(
    (data: KycFormData) =>
      getRequiredFormIds(
        data.identification,
        data.formK,
        isFormKRequired,
        isFormARequired
      ),
    []
  );

  // Form data hook
  const formDataHook = useKycFormData({
    isFormComplete: isFormCompleteWithTranslation,
    setActiveTab,
    getRequiredFormIds: getRequiredFormIdsCallback,
    initialData,
  });

  // Recalculate required form IDs based on current form data
  const requiredFormIds = useMemo(
    () =>
      getRequiredFormIds(
        formDataHook.formData.identification,
        formDataHook.formData.formK,
        isFormKRequired,
        isFormARequired
      ),
    [formDataHook.formData.identification, formDataHook.formData.formK]
  );

  // Redirect to default tab when current tab is no longer required
  useEffect(() => {
    if (!requiredFormIds.includes(activeTab)) {
      setActiveTab(DEFAULT_TAB);
    }
  }, [requiredFormIds, activeTab]);

  // Navigation hook with dynamic requiredFormIds
  const navigation = useKycFormNavigation({
    requiredFormIds,
    activeTab,
    setActiveTab,
  });

  // Submission hook
  const submission = useKycFormSubmissionHook({
    token,
    formData: formDataHook.formData,
    communicationLanguage: formDataHook.communicationLanguage,
    setCommunicationLanguageError: formDataHook.setCommunicationLanguageError,
    requiredFormIds,
    activeTab,
    setActiveTab,
    identificationFormRef,
    formKRef,
    formARef,
    onSubmit,
    onError,
    isAdminEdit,
  });

  // Get effective validation state with memoized translation function
  const getEffectiveValidationStateWithTranslation = useCallback(
    (propState: ValidationState, formType: string) => {
      const tFormsFunc = (key: string) => tForms.t(key);
      return getEffectiveValidationState(
        propState,
        formType,
        formDataHook.formData,
        tFormsFunc
      );
    },
    [formDataHook.formData, tForms]
  );

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className='flex items-center gap-2 mb-4 text-lg'>
          <Shield className='h-5 w-5' />
          {tForms.t('kyc.pageTitle')}
        </CardTitle>
        <CardDescription>
          <p className='pb-2'>{tForms.t('kyc.description.purpose')}</p>
          <p className='pb-2'>{tForms.t('kyc.description.compliance')}</p>
          <p className='pb-2'>{tForms.t('kyc.description.support')}</p>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
          <KycTabsList
            tabs={navigation.tabs}
            getEffectiveValidationState={
              getEffectiveValidationStateWithTranslation
            }
            identificationValidationState={identificationValidationState}
            formKValidationState={formKValidationState}
            formAValidationState={formAValidationState}
            canNavigateToTab={navigation.canNavigateToTab}
          />

          <KycTabContent
            formData={formDataHook.formData}
            identificationFormRef={identificationFormRef}
            formKRef={formKRef}
            formARef={formARef}
            onIdentificationSubmit={formDataHook.handleIdentificationSubmit}
            onIdentificationError={formDataHook.handleIdentificationError}
            onIdentificationChange={formDataHook.handleIdentificationChange}
            onFormKSubmit={formDataHook.handleFormKSubmit}
            onFormKError={formDataHook.handleFormKError}
            onFormKChange={formDataHook.handleFormKChange}
            onFormASubmit={formDataHook.handleFormASubmit}
            onFormAError={formDataHook.handleFormAError}
            onFormAChange={formDataHook.handleFormAChange}
          />
        </Tabs>

        {/* Communication Language Selection */}
        <CommunicationLanguageSelector
          value={formDataHook.communicationLanguage}
          onValueChange={formDataHook.handleCommunicationLanguageChange}
          error={formDataHook.communicationLanguageError}
          className='mt-8 pt-6 border-t'
        />

        {/* Navigation Buttons */}
        <FormNavigationButtons
          activeTab={activeTab}
          tabs={navigation.tabs}
          getCurrentTabIndex={navigation.getCurrentTabIndex}
          goToPreviousTab={navigation.goToPreviousTab}
          goToNextTab={navigation.goToNextTab}
          onSubmit={submission.handleManualSubmit}
          isSubmitting={submission.kycFormSubmission.isPending}
        />

        {/* Form Completion Status */}
        <FormProgressIndicator
          requiredFormIds={requiredFormIds}
          formData={formDataHook.formData}
          areAllFormsComplete={submission.areAllFormsComplete}
          kycFormSubmission={submission.kycFormSubmission}
          onRetrySubmit={submission.handleManualSubmit}
        />
      </CardContent>
    </Card>
  );
}

export default KycFormTabs;
